import { routeVariantSchema } from './route.js';
import { shiftAggregateSchema, tripSegmentSchema } from './shift.js';
/**
 * Exact production reader that the compatibility rollout must be able to
 * roll back to after every C1 write.
 */
export const PRODUCTION_A6_ROLLBACK_RELEASE_SHA = 'a6f75968d01ae3a02d2431b6045e0fe67b3a966d';
/**
 * The current reader deliberately accepts learned defaults after their live
 * use count falls below three. The production a6 reader does not, so the C1
 * writer must reject that richer state before persistence rather than erase
 * the user's durable preference.
 */
export const productionA6RollbackRouteVariantSchema = routeVariantSchema.superRefine((value, context) => {
    if (value.default_for_context && value.explicit_confirmation_count < 3) {
        context.addIssue({
            code: 'custom',
            path: ['default_for_context'],
            message: 'the production a6 rollback reader requires three explicit confirmations',
        });
    }
});
/** The production a6 strict segment object predates route_draft_snapshot. */
export const productionA6RollbackTripSegmentSchema = tripSegmentSchema.superRefine((value, context) => {
    if (Object.hasOwn(value, 'route_draft_snapshot')) {
        context.addIssue({
            code: 'custom',
            path: ['route_draft_snapshot'],
            message: 'the production a6 rollback reader does not accept route_draft_snapshot',
        });
    }
});
export const productionA6RollbackShiftAggregateSchema = shiftAggregateSchema.superRefine((value, context) => {
    value.segments.forEach((segment, index) => {
        const parsed = productionA6RollbackTripSegmentSchema.safeParse(segment);
        if (parsed.success)
            return;
        for (const issue of parsed.error.issues) {
            context.addIssue({
                ...issue,
                path: ['segments', index, ...issue.path],
            });
        }
    });
});
/**
 * A null current-wire marker carries no draft payload and can be contracted
 * losslessly. A non-null draft snapshot has no production-a6 representation
 * and must remain fail-closed until the expanded writer is activated.
 */
export function projectShiftAggregateForProductionA6Rollback(input) {
    const value = shiftAggregateSchema.parse(input);
    const segments = value.segments.map((segment) => {
        if (!Object.hasOwn(segment, 'route_draft_snapshot'))
            return segment;
        const routeDraftSnapshot = segment.route_draft_snapshot;
        if (routeDraftSnapshot === undefined) {
            throw new Error('the production a6 rollback projection received an undefined route draft');
        }
        if (routeDraftSnapshot !== null &&
            !isEmptyRouteDraftSnapshotForProductionA6Rollback(routeDraftSnapshot)) {
            throw new Error('the production a6 rollback wire cannot represent a route draft snapshot');
        }
        const { route_draft_snapshot: omitted, ...rollbackSafe } = segment;
        void omitted;
        return rollbackSafe;
    });
    return productionA6RollbackShiftAggregateSchema.parse({ ...value, segments });
}
/** Generated UUIDs in a completely empty route draft carry no user intent. */
export function isEmptyRouteDraftSnapshotForProductionA6Rollback(snapshot) {
    return (snapshot.origin.name === '' &&
        snapshot.origin.country_code === 'CZ' &&
        snapshot.origin.key_identifier === null &&
        snapshot.origin.carried_location_snapshot === null &&
        snapshot.origin.catalog_document_id === null &&
        snapshot.origin.catalog_version === null &&
        snapshot.via.length === 0 &&
        snapshot.destination === null &&
        snapshot.route_selection === null &&
        snapshot.route_seed === null &&
        (snapshot.snapshot_version === 'route_draft_snapshot.v1' ||
            snapshot.route_copy_source === null) &&
        snapshot.distance_input_mode === 'none' &&
        snapshot.distance_metres === null);
}
//# sourceMappingURL=rollback-compatibility.js.map